import { type RefObject, useCallback, useEffect, useRef } from "react";

/**
 * A function that computes the desired size from the current mouse position.
 * Receives the MouseEvent so you can derive size from absolute coordinates.
 *
 * @example
 * // Right-anchored panel: width = viewport right edge minus mouse X
 * const getSize = (e: MouseEvent) => window.innerWidth - e.clientX;
 *
 * @example
 * // Left-anchored panel: width = mouse X minus left edge
 * const getSize = (e: MouseEvent) => e.clientX - panelLeftEdge;
 */
type ComputeSize = (e: MouseEvent) => number;

export interface UseResizableArgs {
  /** Ref to the element being resized */
  elementRef: RefObject<HTMLElement | null>;
  /** The CSS property to resize (e.g. "width" or "height") */
  property: "width" | "height";
  /** Function that computes the desired size from the mouse position */
  computeSize: ComputeSize;
  /** Minimum allowed size in pixels */
  minSize?: number;
  /** Maximum allowed size in pixels */
  maxSize?: number;
  /** Called with the final clamped size when the drag ends */
  onResizeEnd?: (size: number) => void;
  /** Whether resizing is enabled. Defaults to true. */
  enabled?: boolean;
}

/**
 * Generic hook that makes an element resizable via mouse drag.
 *
 * Performs direct DOM mutations during the drag for zero-lag rendering,
 * and disables CSS transitions on the target property while dragging.
 * Calls `onResizeEnd` with the final size once on mouseup.
 *
 * The caller provides a `computeSize` function that derives the desired size
 * from the mouse position, making this hook layout-agnostic.
 *
 * @returns `onDragStart` — attach this as `onMouseDown` on your drag handle element.
 *
 * @example
 * ```tsx
 * const panelRef = useRef<HTMLDivElement>(null);
 *
 * const { onDragStart } = useResizable({
 *   elementRef: panelRef,
 *   property: "width",
 *   computeSize: (e) => window.innerWidth - e.clientX,
 *   minSize: 150,
 *   maxSize: 600,
 *   onResizeEnd: (width) => saveWidth(width),
 * });
 *
 * return (
 *   <div ref={panelRef} style={{ width: 250 }}>
 *     <div onMouseDown={onDragStart} className="cursor-col-resize" />
 *   </div>
 * );
 * ```
 */
export const useResizable = ({
  elementRef,
  property,
  computeSize,
  minSize = 0,
  maxSize = Infinity,
  onResizeEnd,
  enabled = true,
}: UseResizableArgs) => {
  const isDragging = useRef(false);
  const currentSize = useRef(0);

  // Keep callbacks in refs so the mousemove/mouseup listeners never go stale
  const computeSizeRef = useRef(computeSize);
  computeSizeRef.current = computeSize;
  const onResizeEndRef = useRef(onResizeEnd);
  onResizeEndRef.current = onResizeEnd;

  const clamp = useCallback((value: number) => Math.min(maxSize, Math.max(minSize, value)), [minSize, maxSize]);

  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || !elementRef.current) return;
      isDragging.current = true;
      // Disable CSS transition on the resized property so it tracks the mouse instantly
      elementRef.current.style.transition = "none";
      e.preventDefault();
    },
    [enabled, elementRef]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || !elementRef.current) return;
      const next = clamp(computeSizeRef.current(e));
      currentSize.current = next;
      elementRef.current.style[property] = `${next}px`;
    };

    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      // Re-enable CSS transitions
      if (elementRef.current) elementRef.current.style.transition = "";
      onResizeEndRef.current?.(currentSize.current);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [elementRef, property, clamp]);

  return { onDragStart };
};
